import React, { useMemo, useRef, useState } from 'react';
import {
    AttachmentResolver,
    CommentThread,
    CommentThreadProps,
    CommentThreadType,
    ThreadInfo,
    TopBarButtonsDefintion,
    UploadAttachmentFile,
} from '@ygtechnologies/bygr-frontend-chat';
import { ChatFileUploadWizard } from '@/components/FileUploadWizard';
import { Trans } from '@lingui/react/macro';
import CommentTopBarUnitDocuments from './CommentTopBarUnitDocuments';
import theme from './CommentThreadWrapper.module.scss';
import { classnames } from '@ygtechnologies/bygr-frontend-component-library';

export interface CommentThreadWrapperProps extends Omit<CommentThreadProps, 'onAttachmentUpload'> {
    disableDocumentAccess?: boolean;
    variant?: CommentThreadWrapperVariant;
}

type CommentThreadWrapperVariant = 'default' | 'flat' | 'buyer';

export const CommentThreadWrapper: React.FC<CommentThreadWrapperProps> = ({
    onThreadLoad,
    disableDocumentAccess = false,
    wrapperClassName,
    variant = 'default',
    ...rest
}) => {
    const [uploadOpen, setUploadOpen] = useState<boolean>(false);
    const attachmentContinue = useRef<AttachmentResolver | null>(null);
    const [threadInfo, setThreadInfo] = useState<ThreadInfo | null>();

    const handleAttachmentOpen = (cont: AttachmentResolver) => {
        attachmentContinue.current = cont;
        setUploadOpen(true);
    };

    const handleThreadLoad = (info: ThreadInfo) => {
        onThreadLoad?.(info);
        setThreadInfo(info);
    };

    const buttons = useMemo<TopBarButtonsDefintion[]>(() => {
        const showUnitDocuments = threadInfo && !threadInfo.isBuyer && threadInfo.type !== CommentThreadType.Project;
        if (showUnitDocuments) {
            return [
                (context) => <CommentTopBarUnitDocuments residentialUnitId={context.threadInfo.residentialUnitId} />,
                'show-attachments',
            ];
        }
        return ['show-attachments'];
    }, [threadInfo]);

    return (
        <>
            <CommentThread
                {...rest}
                onThreadLoad={handleThreadLoad}
                onAttachmentUpload={handleAttachmentOpen}
                topBarOptions={{ buttons }}
                wrapperClassName={classnames(wrapperClassName, theme.wrapper, theme[variant])}
            />

            <ChatFileUploadWizard
                residentialUnitId={threadInfo?.residentialUnitId}
                disableDocumentAccess={disableDocumentAccess}
                onFileChange={(files) => {
                    attachmentContinue.current!(files.map((x) => new UploadAttachmentFile(x)));
                }}
                isOpen={uploadOpen}
                toggle={() => {
                    setUploadOpen(false);
                }}
            />
        </>
    );
};
